import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  jimiAbrirCrear,
  jimiCapturarManual,
  jimiGenerarEvidencia,
  jimiPrefill,
  jimiRevisar,
  type JimiCategoria,
  type JimiModeloGrupo,
  type JimiObjetoQueja,
  type JimiPrefill,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { copiarTexto } from "@/lib/copiar";
import CorreosParaIA from "@/components/correo/CorreosParaIA";
import { ayudaTicketJimi } from "@/components/ayuda/vistas/ticketJimi";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

/**
 * Modal "Ticket en Jimi": crea un workOrder en el sistema de soporte de Jimi
 * (ticket.jimiiot.com) desde el caso, o registra a mano un numero ya existente.
 * Dos modos (segmented control). Casi full-screen (size xl) porque trae muchos
 * datos: titulo + IMEIs + etiqueta + descripcion + la BITACORA como referencia.
 * El ticket debe verse hecho a mano (sin marcas internas). Ver docs/feature-jimi-ticket/.
 *
 * Desde ~2026-09-10 Jimi exige el «objeto de la queja» (el modelo de su catálogo) y de ahí
 * saca la línea de producto; y, como su portal, no acepta modelos revueltos en un ticket.
 * El prefill trae la revisión de los equipos para AVISAR antes de llenar nada; el alta
 * vuelve a revisar (esa es la autoridad).
 */
export default function AbrirTicketJimiModal({
  casoId,
  onClose,
  onDone,
  modoInicial = "crear",
  numeroInicial = "",
  sinEquipos = false,
}: {
  casoId: number;
  onClose: () => void;
  onDone: () => void;
  modoInicial?: "crear" | "manual";
  numeroInicial?: string;
  /** El caso no trae IMEIs: solo se puede registrar el número de un ticket abierto a mano. */
  sinEquipos?: boolean;
}) {
  const toast = useToast();
  const iaHabilitada = useAuth((s) => s.user?.ai_comentarios_enabled);

  // Si viene numeroInicial, es EDICION de un numero capturado a mano: se abre en
  // modo manual y se oculta el toggle (no se puede "crear" si ya hay numero).
  const editando = numeroInicial.length > 0;
  const [modo, setModo] = useState<"crear" | "manual">(modoInicial);
  const [pf, setPf] = useState<JimiPrefill | null>(null);
  const [cargandoPf, setCargandoPf] = useState(true);
  const [errorPf, setErrorPf] = useState<string | null>(null);
  const [intento, setIntento] = useState(0); // para reintentar el prellenado

  // campos editables (modo crear)
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [etiquetaId, setEtiquetaId] = useState<number | "">("");
  const [objetoCode, setObjetoCode] = useState(""); // «objeto de la queja» = modelo en Jimi
  const [numero, setNumero] = useState(numeroInicial); // modo manual
  const [enviando, setEnviando] = useState(false);
  const [generandoIA, setGenerandoIA] = useState(false);
  // Correos del caso que la IA puede leer al redactar la apertura. En cero
  // por decisión de Joe: él prende los que sirven.
  const [correosIA, setCorreosIA] = useState<number[]>([]);
  const [verBitacora, setVerBitacora] = useState(true);
  const [evidenciaUrl, setEvidenciaUrl] = useState<string | null>(null);
  const [generandoEvid, setGenerandoEvid] = useState(false);

  useEffect(() => {
    // Editar un número manual no necesita el prellenado; sin equipos, el servidor ni
    // lo arma (responde 422: el ticket de Jimi se construye con la lista de IMEIs).
    if (editando || sinEquipos) {
      setCargandoPf(false);
      return;
    }
    let vivo = true;
    setCargandoPf(true);
    setErrorPf(null);
    (async () => {
      try {
        const data = await conTimeout(jimiPrefill(casoId), 15000);
        if (!vivo) return;
        setPf(data);
        setTitulo(data.titulo);
        setDescripcion(conEvidencia(data.descripcion, data.evidencia_url));
        setEtiquetaId(data.sugerencia_etiqueta_dict_data_id ?? "");
        setObjetoCode(data.sugerencia_objeto_queja ?? "");
        setEvidenciaUrl(data.evidencia_url);
      } catch (err) {
        if (vivo)
          setErrorPf(
            err instanceof ApiError
              ? err.detail
              : err instanceof Error
                ? err.message
                : "No se pudo cargar el prellenado.",
          );
      } finally {
        if (vivo) setCargandoPf(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [casoId, editando, sinEquipos, intento]);

  // opciones del select: arbol de 2 niveles -> optgroups
  const opciones = useMemo(() => aplanarCategorias(pf?.categorias ?? []), [pf]);
  const lineas = useMemo(() => agruparPorLinea(pf?.objetos_queja ?? []), [pf]);
  const objeto = pf?.objetos_queja.find((o) => o.code === objetoCode) ?? null;
  // Lo que Jimi rechazaría de todos modos: se dice ANTES de llenar el formulario.
  const multimodelo = (pf?.modelos.length ?? 0) > 1;
  const noValidos = pf?.imeis_no_validos ?? [];
  // Sin catálogo no hay modelo que elegir: se dice y se ofrece recargar.
  const sinCatalogo = pf != null && pf.objetos_queja.length === 0;
  const bloqueado = multimodelo || noValidos.length > 0 || sinCatalogo;

  async function generarIA() {
    setGenerandoIA(true);
    try {
      const { texto } = await api.post<{ texto: string }>(
        `/api/casos/${casoId}/correos/generar-comentario`,
        {
          destinatario: "jimi",
          guia: conEvidencia(descripcion, null),
          correos_ids: correosIA,
        },
      );
      setDescripcion(conEvidencia(texto, evidenciaUrl));
      toast.success("Descripción generada", "Revísala y ajústala antes de crear.");
    } catch (err) {
      toast.error("No se pudo generar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setGenerandoIA(false);
    }
  }

  async function generarEvidencia() {
    setGenerandoEvid(true);
    try {
      const { evidencia_url } = await jimiGenerarEvidencia(casoId);
      setEvidenciaUrl(evidencia_url);
      setDescripcion((d) => conEvidencia(d, evidencia_url));
      toast.success("Link agregado a la descripción", "ZIP sin contraseña, listo para Jimi.");
    } catch (err) {
      toast.error("No se pudo generar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setGenerandoEvid(false);
    }
  }

  async function crear() {
    if (!etiquetaId) {
      toast.error("Falta la Etiqueta de Problema", "Elige una antes de crear.");
      return;
    }
    if (!objetoCode) {
      toast.error("Falta el modelo en Jimi", "Elige el modelo antes de crear.");
      return;
    }
    setEnviando(true);
    try {
      const r = await jimiAbrirCrear(casoId, {
        titulo: titulo.trim(),
        descripcion: textoAhtml(descripcion),
        categoria_dict_data_id: Number(etiquetaId),
        objeto_queja_code: objetoCode,
        evidencia_url: null, // el link ya va dentro de la descripción (WYSIWYG)
      });
      toast.success(
        r.ya_existia ? "Ticket ya existía en Jimi" : "Ticket creado en Jimi",
        r.work_order_no,
      );
      onDone();
    } catch (err) {
      toast.error("No se pudo abrir en Jimi", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setEnviando(false);
    }
  }

  async function guardarManual() {
    setEnviando(true);
    try {
      const r = await jimiCapturarManual(casoId, numero.trim());
      // El número se guarda solo con validar su forma. Se busca en Jimi enseguida: si está
      // mal tecleado, la revisión diaria fallaría en silencio y nadie se enteraría.
      try {
        await jimiRevisar(casoId);
        toast.success("Número registrado", `${r.work_order_no} · Jimi lo encontró`);
      } catch (err) {
        toast.warning(
          "Número guardado, pero Jimi no lo confirmó",
          `${err instanceof ApiError ? err.detail : "No respondió."} Revisa el número y corrígelo con ✏️ junto a él.`,
        );
      }
      onDone();
    } catch (err) {
      toast.error("No se pudo registrar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setEnviando(false);
    }
  }

  const footer =
    modo === "crear" ? (
      <>
        <button type="button" className="btn-ghost" onClick={onClose} disabled={enviando}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={crear}
          disabled={enviando || cargandoPf || bloqueado}
        >
          {enviando ? "Creando…" : "Crear en Jimi"}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="btn-ghost" onClick={onClose} disabled={enviando}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={guardarManual}
          disabled={enviando || !numero.trim()}
        >
          {enviando ? "Guardando…" : "Guardar número"}
        </button>
      </>
    );

  const tituloModal = editando ? "Editar número de Jimi" : "Ticket en Jimi";

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={tituloModal}
      size={modo === "crear" ? "xl" : "md"}
      ayuda={
        <AyudaVista
          titulo={tituloModal}
          {...ayudaTicketJimi({
            modo,
            editando,
            sinEquipos,
            iaHabilitada: Boolean(iaHabilitada),
          })}
        />
      }
      footer={footer}
    >
      {/* Segmented control: crear / manual (oculto al editar un número manual y sin equipos) */}
      {!editando && !sinEquipos && (
        <div className="mb-4 inline-flex rounded-lg border border-ink-700 bg-ink-800 p-0.5 text-sm">
          {(["crear", "manual"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setModo(m)}
              className={`rounded-md px-3 py-1.5 ${
                modo === m ? "bg-ink-700 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {m === "crear" ? "Crear nuevo" : "Ya tengo el número"}
            </button>
          ))}
        </div>
      )}

      {cargandoPf && <p className="text-sm text-zinc-400">Cargando…</p>}
      {errorPf && (
        <p className="text-sm text-red-400">
          {errorPf}{" "}
          <button
            type="button"
            onClick={() => setIntento((n) => n + 1)}
            className="ml-1 underline hover:text-red-300"
          >
            Reintentar
          </button>
        </p>
      )}

      {/* 50/50 desperdicia: la referencia suele ser UNA nota y el formulario es
          donde se trabaja. El formulario se lleva la parte del león.
          `min-h-0` SOLO desde lg: en móvil el grid y la columna se encogían por debajo de
          su contenido y la bitácora se encimaba sobre la IA y las evidencias (medido: 170px
          a 412px). En móvil manda el alto natural y scrollea el cuerpo del modal; en lg la
          columna del formulario scrollea por dentro. */}
      {pf && modo === "crear" && (
        <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          {/* Columna izquierda: los campos del ticket */}
          <div className="flex flex-col gap-3 lg:min-h-0 lg:overflow-y-auto lg:pr-1 scroll-sutil">
            {multimodelo && <AvisoMultimodelo grupos={pf.modelos} />}
            {noValidos.length > 0 && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                <p className="font-medium text-red-100">
                  {noValidos.length === 1
                    ? "Jimi no reconoce un IMEI de este caso"
                    : `Jimi no reconoce ${noValidos.length} IMEIs de este caso`}
                </p>
                <ListaImeis imeis={noValidos} abierta={noValidos.length <= 8} />
                <p className="mt-1">
                  {noValidos.length === 1
                    ? "Corrígelo o quítalo del caso para poder abrir el ticket."
                    : "Corrígelos o quítalos del caso para poder abrir el ticket."}
                </p>
              </div>
            )}

            <label className="text-sm">
              <span className="mb-1 block text-zinc-400">Título</span>
              <input className="input w-full" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            </label>

            <div className="text-sm">
              <span className="mb-1 block text-zinc-400">Equipos (todos van en el ticket)</span>
              <div className="flex flex-wrap gap-1">
                {pf.imeis.map((im) => (
                  <span key={im} className="rounded border border-ink-700 bg-ink-800 px-2 py-0.5 font-mono text-xs">
                    {im}
                  </span>
                ))}
              </div>
              {!multimodelo && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  {pf.modelos_verificados
                    ? "Jimi solo acepta tickets de un solo modelo a la vez."
                    : "Jimi solo acepta tickets de un solo modelo a la vez. Ahorita no se pudo revisar los equipos con Jimi; se revisan otra vez al crear."}
                </p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-zinc-400">
                  Etiqueta de Problema <span className="text-red-400">*</span>
                  {pf.sugerencia_etiqueta_dict_data_id != null && (
                    <span className="ml-1 text-[10px] text-accent-400">(sugerida automáticamente)</span>
                  )}
                </span>
                <select
                  className="input w-full"
                  value={etiquetaId}
                  onChange={(e) => setEtiquetaId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Elegir…</option>
                  {opciones.map((g) => (
                    <optgroup key={g.nombre} label={g.nombre}>
                      {g.hijos.map((h) => (
                        <option key={h.dict_data_id} value={h.dict_data_id}>
                          {h.nombre}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <span className="mb-1 block text-zinc-400">
                  Modelo en Jimi <span className="text-red-400">*</span>
                  {objetoCode !== "" && objetoCode === pf.sugerencia_objeto_queja && (
                    <span className="ml-1 text-[10px] text-accent-400">(sugerido automáticamente)</span>
                  )}
                </span>
                <select className="input w-full" value={objetoCode} onChange={(e) => setObjetoCode(e.target.value)}>
                  <option value="">Elegir…</option>
                  {lineas.map((g) => (
                    <optgroup key={g.linea_code} label={g.linea_nombre}>
                      {g.modelos.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.nombre}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {sinCatalogo ? (
                  <span className="mt-1 block text-[11px] text-red-300">
                    No se pudo cargar la lista de modelos de Jimi.{" "}
                    <button
                      type="button"
                      onClick={() => setIntento((n) => n + 1)}
                      className="underline hover:text-red-200"
                    >
                      Reintentar
                    </button>
                  </span>
                ) : (
                  <span className="mt-1 block text-[11px] text-zinc-500">
                    {objeto
                      ? `Línea de producto: ${objeto.linea_nombre}. Jimi la toma del modelo.`
                      : "Jimi toma del modelo la línea de producto."}
                  </span>
                )}
              </label>
            </div>

            {/* Sin `min-h-0`: el label no puede ser más bajo que el textarea (su piso de
                20rem), o el textarea se sale y queda debajo del recuadro de la IA. */}
            <label className="text-sm lg:flex lg:flex-1 lg:flex-col">
              <span className="mb-1 block text-zinc-400">
                Descripción detallada <span className="text-red-400">*</span>
              </span>
              {/* Crece con el modal, con piso: es donde se escribe. */}
              <textarea
                className="input min-h-[160px] w-full lg:min-h-[20rem] lg:flex-1"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Describe el problema. Apóyate en la bitácora de la derecha."
              />
            </label>

            {iaHabilitada && (
              <div className="rounded border border-accent-500/30 bg-accent-500/5 p-2">
                <button
                  type="button"
                  onClick={generarIA}
                  disabled={generandoIA}
                  className="inline-flex items-center gap-1.5 rounded-md border border-accent-500/40 bg-accent-500/10 px-2.5 py-1.5 text-xs text-accent-300 hover:bg-accent-500/20 disabled:opacity-50"
                >
                  ✨ {generandoIA ? "Generando…" : "Redactar con IA"}
                </button>
                <p className="mt-1 text-[10px] text-zinc-500">
                  Usa la bitácora como base. Lo que escribas se toma como guía. Queda editable; no se envía solo.
                </p>
              </div>
            )}

            {/* Evidencias */}
            <div className="text-sm">
              <span className="mb-1 block text-zinc-400">Evidencias</span>
              {evidenciaUrl && (
                <a
                  href={evidenciaUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-1.5 block break-all text-xs text-accent-400 hover:underline"
                >
                  🔗 Link de evidencias (ya está en la descripción ↑)
                </a>
              )}
              {pf.puede_generar_evidencia ? (
                <>
                  <button
                    type="button"
                    onClick={generarEvidencia}
                    disabled={generandoEvid}
                    className="rounded-md border border-ink-600 bg-ink-800 px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-ink-700 disabled:opacity-50"
                  >
                    {generandoEvid
                      ? "Generando…"
                      : evidenciaUrl
                        ? "Generar otro link (sin contraseña)"
                        : "Generar link de evidencias (sin contraseña)"}
                  </button>
                  <p className="mt-1 text-[10px] text-zinc-500">
                    Crea un ZIP <strong>sin contraseña</strong> — para que el equipo de Jimi no batalle.
                  </p>
                </>
              ) : (
                !evidenciaUrl && (
                  <p className="text-[11px] text-zinc-500">Sin evidencias en este caso.</p>
                )
              )}
            </div>
          </div>

          {/* Columna derecha: la bitácora interna como referencia */}
          <div className="rounded-lg border border-ink-700 bg-ink-800/50 p-3 lg:flex lg:min-h-0 lg:flex-col">
            <button
              type="button"
              className="mb-2 flex w-full items-center justify-between text-sm font-medium text-zinc-300"
              onClick={() => setVerBitacora((v) => !v)}
            >
              <span>Bitácora interna · {pf.bitacora.length} notas</span>
              <span className="text-xs text-zinc-500">{verBitacora ? "Ocultar" : "Ver"}</span>
            </button>
            {verBitacora && (
              <ul className="flex max-h-[52vh] flex-col gap-2 overflow-y-auto scroll-sutil pr-1
                             lg:max-h-none lg:min-h-0 lg:flex-1">
                {pf.bitacora.length === 0 && (
                  <li className="text-[11px] text-zinc-500">Sin notas en la bitácora.</li>
                )}
                {pf.bitacora.map((n, i) => (
                  <li key={i} className="rounded border border-ink-700 bg-ink-900/60 p-2">
                    <div className="mb-0.5 text-[10px] text-zinc-500">
                      {n.fecha} · {n.autor ?? "—"}
                    </div>
                    <div className="whitespace-pre-wrap text-xs text-zinc-300">{n.texto}</div>
                  </li>
                ))}
              </ul>
            )}

            {iaHabilitada && (
              <CorreosParaIA
                casoId={casoId}
                seleccion={correosIA}
                onCambio={setCorreosIA}
                className="mt-3 border-t border-ink-700 pt-3"
              />
            )}
          </div>
        </div>
      )}

      {modo === "manual" && (
        <div className="max-w-md">
          {sinEquipos && (
            <p className="mb-3 text-sm text-zinc-300">
              Este caso no tiene equipos (IMEIs), y desde aquí el ticket se arma con los equipos
              del caso, así que no se puede crear. Ábrelo en el portal de Jimi y pega aquí su
              número para que el caso lo siga.
            </p>
          )}
          <label className="text-sm">
            <span className="mb-1 block text-zinc-400">
              Número de ticket de Jimi <span className="text-red-400">*</span>
            </span>
            <input
              className="input w-full font-mono"
              name="jimi_numero"
              autoComplete="off"
              spellCheck={false}
              translate="no"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="SA-2026…"
            />
          </label>
          {!sinEquipos && (
            <p className="mt-1 text-[11px] text-zinc-500">
              ¿Ya lo abriste a mano en Jimi? Pega aquí el número tal cual del portal.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// --- helpers ---
/** Corta una promesa si tarda más de `ms` (evita que el modal quede en "Cargando…" eterno). */
function conTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("Se tardó demasiado en cargar.")), ms),
    ),
  ]);
}

/** Aviso que BLOQUEA el alta: el portal de Jimi no deja mezclar modelos en un ticket. */
function AvisoMultimodelo({ grupos }: { grupos: JimiModeloGrupo[] }) {
  // `etiqueta` ya trae «(modelo interno X)» cuando dos grupos se llaman igual para la
  // gente (LL303Pro / LL303PRO): la arma el backend, que es quien decide el bloqueo.
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
      <p className="text-sm font-medium text-amber-100">
        Este caso no se puede abrir en Jimi tal como está
      </p>
      <p className="mt-1">
        En el sistema de tickets de Jimi solo se permite crear tickets de un solo modelo a la
        vez, y este caso trae {grupos.length}:
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {grupos.map((g) => (
          <li key={g.modelo || "sin-modelo"}>
            <span className="font-mono text-amber-100">{g.etiqueta}</span>{" "}
            <span>
              ({g.imeis.length} {g.imeis.length === 1 ? "equipo" : "equipos"})
            </span>
            <ListaImeis imeis={g.imeis} />
          </li>
        ))}
      </ul>
      <p className="mt-1.5">
        Separa los equipos en casos de un solo modelo y abre un ticket por cada uno. Los IMEIs de
        cada modelo se copian con un clic, uno por renglón, listos para pegarlos en el caso nuevo.
      </p>
    </div>
  );
}

/** IMEIs plegables + copiar (uno por renglón: así los acepta el alta de un caso). */
function ListaImeis({ imeis, abierta = false }: { imeis: string[]; abierta?: boolean }) {
  const toast = useToast();
  async function copiar() {
    const ok = await copiarTexto(imeis.join("\n"));
    if (ok) toast.success("IMEIs copiados", `${imeis.length} ${imeis.length === 1 ? "IMEI" : "IMEIs"}`);
    else toast.error("No se pudo copiar", "Selecciónalos a mano.");
  }
  return (
    <details className="mt-1" open={abierta}>
      <summary className="cursor-pointer select-none text-[11px] opacity-80 hover:opacity-100">
        {imeis.length === 1 ? "Ver el IMEI" : `Ver los ${imeis.length} IMEIs`}
      </summary>
      <div className="mt-1 rounded border border-white/10 bg-black/20 p-2">
        <p className="break-words font-mono text-[11px] leading-relaxed">{imeis.join(", ")}</p>
        <button
          type="button"
          onClick={copiar}
          className="mt-1.5 rounded border border-white/20 px-2 py-0.5 text-[11px] hover:bg-white/5"
        >
          {imeis.length === 1 ? "Copiar el IMEI" : `Copiar los ${imeis.length}`}
        </button>
      </div>
    </details>
  );
}

type Grupo = { nombre: string; hijos: JimiCategoria[] };

/** Arbol de 2 niveles -> optgroups. Una raiz sin hijos se vuelve su propio grupo/opcion. */
function aplanarCategorias(arbol: JimiCategoria[]): Grupo[] {
  return arbol.map((c) => ({
    nombre: c.nombre,
    hijos: c.children.length > 0 ? c.children : [c],
  }));
}

type Linea = { linea_code: string; linea_nombre: string; modelos: JimiObjetoQueja[] };

/** Modelos de Jimi -> un optgroup por línea de producto, en el orden de su catálogo. */
function agruparPorLinea(objetos: JimiObjetoQueja[]): Linea[] {
  const lineas: Linea[] = [];
  for (const o of objetos) {
    let l = lineas.find((x) => x.linea_code === o.linea_code);
    if (!l) {
      l = { linea_code: o.linea_code, linea_nombre: o.linea_nombre, modelos: [] };
      lineas.push(l);
    }
    l.modelos.push(o);
  }
  return lineas;
}

/** Pone (o reemplaza) la línea "Evidencias: <url>" al final de la descripción,
 * para que el link sea VISIBLE en la caja (WYSIWYG). Con url null, solo la quita. */
function conEvidencia(desc: string, url: string | null): string {
  const base = (desc || "")
    .split("\n")
    .filter((l) => !l.trim().toLowerCase().startsWith("evidencias:"))
    .join("\n")
    .replace(/\n+$/, "");
  return url ? `${base}\n\nEvidencias: ${url}` : base;
}

/** Texto (parrafos separados por linea en blanco) -> HTML simple para Jimi.
 * Las URLs se vuelven <a> clickeables (para que las evidencias abran en Jimi). */
function textoAhtml(texto: string): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const linkify = (s: string) => s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>');
  return texto
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${linkify(esc(p)).replace(/\n/g, "<br>")}</p>`)
    .join("");
}
